import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAuditLogs } from '../../store/auditLogs';

function AuditLogs() {
  const dispatch = useDispatch();
  const logs = useSelector((state) => state.auditLogs.logs);
  const searchQuery = useSelector((state) => state.ui.searchQuery);
  const sessionUser = useSelector(state => state.session.user);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Filter states
  const [selectedAction, setSelectedAction] = useState('all');
  const [selectedTable, setSelectedTable] = useState('all');
  const [selectedTimeframe, setSelectedTimeframe] = useState('all');

  useEffect(() => {
    dispatch(fetchAuditLogs());
  }, [dispatch]);

  if (!sessionUser?.is_admin) {
    return <div className="p-5 text-3xl">Unauthorized</div>;
  }

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE':
        return 'text-green-600';
      case 'UPDATE':
        return 'text-blue-600';
      case 'DELETE':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const handleShowDetails = (e, log) => {
    e.stopPropagation();
    setSelectedLog(log);
    setShowModal(true);
  };

  // Get unique table names for filter
  const getUniqueTables = () => {
    const tables = logs.map(log => log.table_name);
    return [...new Set(tables)].sort();
  };

  // Get unique actions for filter
  const getUniqueActions = () => {
    const actions = logs.map(log => log.action);
    return [...new Set(actions)].sort();
  };

  // Filter logs based on search query and selected filters
  const filteredLogs = logs.filter(log => {
    // First apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const matchesSearch = (
        (log.user_alpha_code && log.user_alpha_code.toLowerCase().includes(query)) ||
        log.table_name.toLowerCase().includes(query) ||
        String(log.record_id).toLowerCase().includes(query) ||
        log.action.toLowerCase().includes(query) ||
        (log.changed_fields && JSON.stringify(log.changed_fields).toLowerCase().includes(query)) ||
        formatTimestamp(log.timestamp).toLowerCase().includes(query)
      );

      if (!matchesSearch) return false;
    }

    // Then apply radio button filters
    if (selectedAction !== 'all' && log.action !== selectedAction) {
      return false;
    }

    if (selectedTable !== 'all' && log.table_name !== selectedTable) {
      return false;
    }

    if (selectedTimeframe !== 'all') {
      const now = new Date();
      const logDate = new Date(log.timestamp);
      const diffHours = (now - logDate) / (1000 * 60 * 60);

      if (selectedTimeframe === 'today' && diffHours > 24) {
        return false;
      } else if (selectedTimeframe === 'week' && diffHours > 168) { // 7 days
        return false;
      } else if (selectedTimeframe === 'month' && diffHours > 720) { // 30 days
        return false;
      }
    }

    return true;
  });

  const ChangeDetailsModal = () => {
    if (!selectedLog) return null;

    const changesObj = typeof selectedLog.changed_fields === 'string'
      ? JSON.parse(selectedLog.changed_fields)
      : selectedLog.changed_fields;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[80vh] relative">
          <button
            onClick={() => setShowModal(false)}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl z-10"
          >
            ✕
          </button>

          <div className="overflow-y-auto max-h-[calc(80vh-2rem)] pr-4">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-blue-600">Change Details</h2>
            </div>

            <div className="mb-4">
              <p className="text-2xl mb-2">
                <span className="font-medium">Timestamp:</span> {formatTimestamp(selectedLog.timestamp)}
              </p>
              <p className="text-2xl mb-2">
                <span className="font-medium">Table:</span> {selectedLog.table_name}
              </p>
              <p className="text-2xl mb-2">
                <span className="font-medium">Record ID:</span> {selectedLog.record_id}
              </p>
              <p className="text-2xl mb-2">
                <span className={`font-medium ${getActionColor(selectedLog.action)}`}>
                  Action: {selectedLog.action}
                </span>
              </p>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-2xl font-medium mb-4">Changes:</h3>
              {Object.entries(changesObj).map(([key, value]) => (
                <div key={key} className="mb-4 bg-gray-50 p-4 rounded-lg">
                  <p className="text-2xl">
                    <span className="font-medium">{key}:</span>{' '}
                    {typeof value === 'object'
                      ? <pre className="whitespace-pre-wrap text-2xl mt-2">{JSON.stringify(value, null, 2)}</pre>
                      : String(value)
                    }
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-5">
      {/* Dashboard-style header */}
      <div className="mb-10">
        <h1 className="text-4xl font-bold mb-6 text-blue-800">Audit Logs Dashboard</h1>
        <p className="text-2xl text-gray-600 mb-6">
          Showing {filteredLogs.length} audit logs.
          {searchQuery && ` Filtered by search: "${searchQuery}"`}
        </p>
      </div>

      {/* Filter section similar to dashboard */}
      <div className="mb-10 bg-gray-50 p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-6 flex-wrap">
          {/* Action Filter */}
          <div className="flex-1 min-w-[250px]">
            <h3 className="text-2xl font-bold mb-2 text-gray-800">Action Type</h3>
            <div className="flex flex-wrap gap-3">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  id="action-all"
                  value="all"
                  checked={selectedAction === 'all'}
                  onChange={(e) => setSelectedAction(e.target.value)}
                  className="form-radio h-6 w-6 text-blue-600"
                />
                <span className="ml-2 text-lg">All</span>
              </label>
              {getUniqueActions().map(action => (
                <label key={`action-${action}`} className="inline-flex items-center">
                  <input
                    type="radio"
                    id={`action-${action}`}
                    value={action}
                    checked={selectedAction === action}
                    onChange={(e) => setSelectedAction(e.target.value)}
                    className={`form-radio h-6 w-6 ${
                      action === 'CREATE' ? 'text-green-600' :
                      action === 'UPDATE' ? 'text-blue-600' :
                      action === 'DELETE' ? 'text-red-600' : 'text-gray-600'
                    }`}
                  />
                  <span className={`ml-2 text-lg ${getActionColor(action)}`}>{action}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Vertical Separator */}
          <div className="w-px bg-gray-300 self-stretch hidden md:block"></div>

          {/* Table Filter */}
          <div className="flex-1 min-w-[250px]">
            <h3 className="text-2xl font-bold mb-2 text-gray-800">Table</h3>
            <div className="flex flex-wrap gap-3">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  id="table-all"
                  value="all"
                  checked={selectedTable === 'all'}
                  onChange={(e) => setSelectedTable(e.target.value)}
                  className="form-radio h-6 w-6 text-blue-600"
                />
                <span className="ml-2 text-lg">All</span>
              </label>
              {getUniqueTables().map(table => (
                <label key={`table-${table}`} className="inline-flex items-center">
                  <input
                    type="radio"
                    id={`table-${table}`}
                    value={table}
                    checked={selectedTable === table}
                    onChange={(e) => setSelectedTable(e.target.value)}
                    className="form-radio h-6 w-6 text-blue-600"
                  />
                  <span className="ml-2 text-lg">{table}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Vertical Separator */}
          <div className="w-px bg-gray-300 self-stretch hidden md:block"></div>

          {/* Timeframe Filter */}
          <div className="flex-1 min-w-[250px]">
            <h3 className="text-2xl font-bold mb-2 text-gray-800">Timeframe</h3>
            <div className="flex flex-wrap gap-3">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  id="timeframe-all"
                  value="all"
                  checked={selectedTimeframe === 'all'}
                  onChange={(e) => setSelectedTimeframe(e.target.value)}
                  className="form-radio h-6 w-6 text-blue-600"
                />
                <span className="ml-2 text-lg">All Time</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  id="timeframe-today"
                  value="today"
                  checked={selectedTimeframe === 'today'}
                  onChange={(e) => setSelectedTimeframe(e.target.value)}
                  className="form-radio h-6 w-6 text-blue-600"
                />
                <span className="ml-2 text-lg">Last 24 Hours</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  id="timeframe-week"
                  value="week"
                  checked={selectedTimeframe === 'week'}
                  onChange={(e) => setSelectedTimeframe(e.target.value)}
                  className="form-radio h-6 w-6 text-blue-600"
                />
                <span className="ml-2 text-lg">Last 7 Days</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  id="timeframe-month"
                  value="month"
                  checked={selectedTimeframe === 'month'}
                  onChange={(e) => setSelectedTimeframe(e.target.value)}
                  className="form-radio h-6 w-6 text-blue-600"
                />
                <span className="ml-2 text-lg">Last 30 Days</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="bg-white shadow-md rounded-lg p-6 border-l-4 border-green-500">
          <h3 className="text-xl text-gray-500 mb-1">Total Records</h3>
          <p className="text-4xl font-bold">{filteredLogs.length}</p>
        </div>
        <div className="bg-white shadow-md rounded-lg p-6 border-l-4 border-blue-500">
          <h3 className="text-xl text-gray-500 mb-1">Tables Affected</h3>
          <p className="text-4xl font-bold">{getUniqueTables().length}</p>
        </div>
        <div className="bg-white shadow-md rounded-lg p-6 border-l-4 border-purple-500">
          <h3 className="text-xl text-gray-500 mb-1">Action Types</h3>
          <p className="text-4xl font-bold">{getUniqueActions().length}</p>
        </div>
      </div>

      {/* Logs Table */}
      <div className="loadTrailer">
        <div className="loadTrailer_header grid-cols-[1fr_1fr_1fr_1fr_1fr_2fr]">
          <div className="text-2xl">Timestamp</div>
          <div className="text-2xl">User Performed Changes</div>
          <div className="text-2xl">Table</div>
          <div className="text-2xl">Record ID</div>
          <div className="text-2xl">Action</div>
          <div className="text-2xl">Changes</div>
        </div>
        {filteredLogs.length > 0 ? (
          filteredLogs.map((log) => (
            <div
              key={log.id}
              className="grid-row grid-cols-[1fr_1fr_1fr_1fr_1fr_2fr] border-2 border-gray-200 hover:shadow-xl
                hover:bg-blue-100 hover:border-blue-500 hover:scale-[1.02]
                transition-all duration-200 cursor-pointer"
            >
              <div className="text-2xl">{formatTimestamp(log.timestamp)}</div>
              <div className="text-2xl">{log.user_alpha_code || 'Unknown'}</div>
              <div className="text-2xl">{log.table_name}</div>
              <div className="text-2xl">{log.record_id}</div>
              <div className={`text-2xl font-medium ${getActionColor(log.action)}`}>
                {log.action}
              </div>
              <div className="text-2xl p-2">
                {log.changed_fields ? (
                  <button
                    onClick={(e) => handleShowDetails(e, log)}
                    className="text-blue-600 hover:text-blue-800 text-2xl"
                  >
                    Show Details
                  </button>
                ) : (
                  <span className="text-gray-500 italic text-2xl">No changes</span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="p-5 text-center">
            <p className="text-2xl text-gray-600">No audit logs found matching your search criteria.</p>
          </div>
        )}
      </div>
      {showModal && <ChangeDetailsModal />}
    </div>
  );
}

export default AuditLogs;
